import { formatPriceLak } from "@/shared/lib/format-price";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import type { ProductDTO } from "../api/client";
import { ProductKindBadge } from "./ProductKindBadge";
import type { ProductCatalogConfig } from "./product-catalog-config";

function getProductImageUrl(row: ProductDTO): string | null {
  const image = row.images?.[0];
  return image?.fileKey ? resolveImageSrc(image.fileKey) : null;
}

function getDisplayName(row: ProductDTO): { name: string; subtitle: string } {
  const name = row.name as { lo?: string; en?: string; th?: string };
  const primaryName = name.lo || name.en || name.th || "";
  const category = row.categories?.[0]?.name as
    | { lo?: string; en?: string; th?: string }
    | undefined;
  const subtitle = category
    ? category.lo || category.en || category.th || ""
    : "";
  return { name: primaryName, subtitle };
}

function formatPrice(price?: number): string {
  if (!price) return "—";
  return "LAK " + formatPriceLak(price);
}

function statusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive"> = {
    draft: "secondary",
    active: "default",
    archived: "destructive",
  };
  const labels: Record<string, string> = {
    draft: "ແບບຮ່າງ",
    active: "ເປີດໃຊ້ງານ",
    archived: "ເກັບຖາວອນ",
  };
  return (
    <Badge variant={variants[status] ?? "default"}>
      {labels[status] ?? status}
    </Badge>
  );
}

type ProductsTableProps = {
  config: ProductCatalogConfig;
  canManage: boolean;
  isLoading: boolean;
  data: ProductDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: ProductDTO) => void;
  onEdit: (row: ProductDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function ProductsTable({
  config,
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: ProductsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ProductDTO>[] = [
    {
      id: "image",
      header: "",
      cell: ({ row }) => {
        const imageUrl = getProductImageUrl(row.original);
        if (!imageUrl) {
          return (
            <div className="flex size-12 items-center justify-center rounded-md bg-muted">
              <span className="text-muted-foreground text-xs">No img</span>
            </div>
          );
        }
        return (
          <img
            src={imageUrl}
            alt={getDisplayName(row.original).name}
            className="size-12 rounded-md object-cover"
          />
        );
      },
      size: 60,
      enableSorting: false,
    },
    {
      id: "name",
      header: "ຊື່",
      cell: ({ row }) => {
        const { name, subtitle } = getDisplayName(row.original);
        return (
          <div>
            <div className="font-medium text-sm">{name}</div>
            {subtitle && (
              <div className="text-muted-foreground text-xs">{subtitle}</div>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-xs">
          {row.original.slug}
        </span>
      ),
      size: 150,
    },
    ...(config.showKindColumn
      ? [
          {
            id: "kind",
            header: "ປະເພດ",
            cell: ({ row }: { row: { original: ProductDTO } }) => (
              <ProductKindBadge
                kind={row.original.kind}
                isSellable={row.original.isSellable}
                isShowcaseEligible={row.original.isShowcaseEligible}
              />
            ),
            size: 150,
            enableSorting: false,
          } satisfies TanstackReactTable.ColumnDef<ProductDTO>,
        ]
      : []),
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => statusBadge(row.original.status),
      size: 120,
    },
    ...(config.showPriceColumns
      ? [
          {
            id: "minPrice",
            header: "ລາຄາຕ່ຳສຸດ",
            cell: ({ row }: { row: { original: ProductDTO } }) => (
              <span className="text-right font-medium text-sm">
                {row.original.minPrice
                  ? formatPrice(row.original.minPrice)
                  : "—"}
              </span>
            ),
            size: 100,
          } satisfies TanstackReactTable.ColumnDef<ProductDTO>,
        ]
      : []),
    ...(config.showVariantsColumn
      ? [
          {
            id: "variants",
            header: "Variants",
            cell: ({ row }: { row: { original: ProductDTO } }) => (
              <span className="text-right text-muted-foreground text-sm">
                {row.original.variantCount ?? 0}
              </span>
            ),
            size: 80,
          } satisfies TanstackReactTable.ColumnDef<ProductDTO>,
        ]
      : []),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const actions = [
          {
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          },
          ...(canManage
            ? [
                {
                  label: "ແກ້ໄຂ",
                  icon: <EditIcon className="h-4 w-4" />,
                  onClick: () => onEdit(row.original),
                },
                {
                  label: "ລຶບ",
                  icon: <TrashIcon className="h-4 w-4" />,
                  onClick: () => onDelete(row.original.id),
                  variant: "destructive" as const,
                },
              ]
            : []),
        ];
        return <RowActions actions={actions} maxInline={3} />;
      },
      size: 140,
      enableSorting: false,
    },
  ];

  return (
    <div data-tourid="products-table">
      <DataTable<ProductDTO, unknown>
        noDataMessage={
          config.mode === "retail"
            ? "ບໍ່ພົບສິນຄ້າ"
            : "ບໍ່ພົບແບບຕົວຢ່າງ"
        }
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id as string, !!s[0]?.desc);
        }}
      />
    </div>
  );
}
